<div class="cron-options">

    <!-- ── Mode toggle: Presets / Builder / Expert ─────────────────────────── -->
    <mat-button-toggle-group [value]="activeMode" (change)="activeMode = $event.value"
                             hideSingleSelectionIndicator class="mode-toggle">
        <mat-button-toggle value="PRESETS"
                           matTooltip="One-click common patterns">
            <mat-icon>flash_on</mat-icon>&nbsp;Presets
        </mat-button-toggle>
        <mat-button-toggle value="BUILDER"
                           matTooltip="Plain-language inputs that compile to a cron expression">
            <mat-icon>build</mat-icon>&nbsp;Builder
        </mat-button-toggle>
        <mat-button-toggle value="EXPERT"
                           matTooltip="Edit the raw 6-field cron expression directly">
            <mat-icon>code</mat-icon>&nbsp;Expression
        </mat-button-toggle>
    </mat-button-toggle-group>

    <!-- ── PRESETS — one click sets the expression ────────────────────────── -->
    <div *ngIf="activeMode === 'PRESETS'" class="presets-grid">
        <button *ngFor="let p of presets" type="button"
                class="preset-chip" mat-stroked-button
                [class.active]="cronControl.value === p.expr"
                (click)="setPreset(p.expr)"
                [matTooltip]="p.expr">
            {{ p.label }}
        </button>
    </div>

    <!-- ── BUILDER — compiled-from-friendly-inputs ────────────────────────── -->
    <div *ngIf="activeMode === 'BUILDER'" class="builder">
        <mat-form-field appearance="outline" class="builder-mode">
            <mat-label>Pattern</mat-label>
            <mat-select [formControl]="builderMode">
                <mat-option value="EVERY_N_MINUTES">Every N minutes</mat-option>
                <mat-option value="EVERY_N_HOURS">Every N hours</mat-option>
                <mat-option value="DAILY_AT">Daily at HH:MM (optional weekday filter)</mat-option>
                <mat-option value="WEEKLY_AT">Weekly on a single weekday at HH:MM</mat-option>
                <mat-option value="MONTHLY_AT_DAY">Monthly on day-of-month at HH:MM</mat-option>
                <mat-option value="MONTHLY_NTH_WEEKDAY">Monthly on the Nth weekday at HH:MM</mat-option>
            </mat-select>
        </mat-form-field>

        <!-- EVERY_N_MINUTES / EVERY_N_HOURS — a single integer input -->
        <ng-container *ngIf="builderMode.value === 'EVERY_N_MINUTES' || builderMode.value === 'EVERY_N_HOURS'">
            <mat-form-field appearance="outline" class="builder-narrow">
                <mat-label>Every N</mat-label>
                <input matInput type="number" [formControl]="everyN" min="1"
                       [max]="builderMode.value === 'EVERY_N_MINUTES' ? 59 : 23" />
                <span matTextSuffix>{{ builderMode.value === 'EVERY_N_MINUTES' ? 'minutes' : 'hours' }}</span>
            </mat-form-field>
        </ng-container>

        <!-- TIME-OF-DAY (used by every mode below this point) -->
        <ng-container *ngIf="builderMode.value === 'DAILY_AT' ||
                             builderMode.value === 'WEEKLY_AT' ||
                             builderMode.value === 'MONTHLY_AT_DAY' ||
                             builderMode.value === 'MONTHLY_NTH_WEEKDAY'">
            <div class="builder-row">
                <mat-form-field appearance="outline" class="builder-narrow">
                    <mat-label>Hour</mat-label>
                    <mat-select [formControl]="atHour">
                        <mat-option *ngFor="let h of hours" [value]="h">{{ h | number:'2.0' }}</mat-option>
                    </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="builder-narrow">
                    <mat-label>Minute</mat-label>
                    <mat-select [formControl]="atMinute">
                        <mat-option *ngFor="let m of minutes" [value]="m">{{ m | number:'2.0' }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </div>
        </ng-container>

        <!-- DAILY_AT — optional weekday filter.
             Always-mounted chip-listbox toggled via [hidden] (same fix
             as hourly Minute Marks). *ngIf around a multi-select chip-
             listbox races Material's [multiple] init and downgrades to
             single-select after the listbox is recreated. -->
        <div [hidden]="builderMode.value !== 'DAILY_AT'">
            <div class="builder-subtitle">On weekdays (none = all days)</div>
            <mat-chip-listbox [multiple]="true" [formControl]="dailyWeekdays">
                <mat-chip-option *ngFor="let d of weekdays" [value]="d">
                    {{ d | titlecase }}
                </mat-chip-option>
            </mat-chip-listbox>
        </div>

        <!-- WEEKLY_AT — single weekday -->
        <mat-form-field appearance="outline" class="builder-narrow"
                        *ngIf="builderMode.value === 'WEEKLY_AT'">
            <mat-label>Day of week</mat-label>
            <mat-select [formControl]="weeklyDay">
                <mat-option *ngFor="let d of weekdays" [value]="d">{{ d | titlecase }}</mat-option>
            </mat-select>
        </mat-form-field>

        <!-- MONTHLY_AT_DAY — day-of-month with 'L' option -->
        <mat-form-field appearance="outline" class="builder-narrow"
                        *ngIf="builderMode.value === 'MONTHLY_AT_DAY'">
            <mat-label>Day of month</mat-label>
            <mat-select [formControl]="monthDay">
                <mat-option *ngFor="let d of daysOfMonth" [value]="d">
                    {{ d === 'L' ? 'Last day' : d }}
                </mat-option>
            </mat-select>
        </mat-form-field>

        <!-- MONTHLY_NTH_WEEKDAY — Nth occurrence + weekday -->
        <ng-container *ngIf="builderMode.value === 'MONTHLY_NTH_WEEKDAY'">
            <div class="builder-row">
                <mat-form-field appearance="outline" class="builder-narrow">
                    <mat-label>Occurrence</mat-label>
                    <mat-select [formControl]="nthOccurrence">
                        <mat-option value="1">First</mat-option>
                        <mat-option value="2">Second</mat-option>
                        <mat-option value="3">Third</mat-option>
                        <mat-option value="4">Fourth</mat-option>
                        <mat-option value="L">Last</mat-option>
                    </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="builder-narrow">
                    <mat-label>Weekday</mat-label>
                    <mat-select [formControl]="nthWeekday">
                        <mat-option *ngFor="let d of weekdays" [value]="d">{{ d | titlecase }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </div>
        </ng-container>
    </div>

    <!-- ── EXPERT — raw expression input + help ──────────────────────────── -->
    <div *ngIf="activeMode === 'EXPERT'" class="expert">
        <mat-form-field appearance="outline" class="full-width">
            <mat-label>Cron Expression</mat-label>
            <input matInput [formControl]="cronControl" placeholder="0 */15 * * * *" autocomplete="off" />
            <mat-hint>6 fields: <code>sec min hour day-of-month month day-of-week</code> &nbsp;or a macro (<code>&#64;daily</code>, …)</mat-hint>
            <mat-error *ngIf="!isValid">Not a valid Spring cron expression — see help below.</mat-error>
        </mat-form-field>
    </div>

    <!-- ── Live preview — shown in every mode ──────────────────────────────── -->
    <div class="preview-row" *ngIf="cronControl.value">
        <div class="preview-expr">
            <mat-icon class="preview-icon">code</mat-icon>
            <code>{{ cronControl.value }}</code>
        </div>
        <div class="preview-text" *ngIf="isValid && previewText">
            <mat-icon class="preview-icon">translate</mat-icon>
            <span>{{ previewText }}</span>
        </div>
        <div class="preview-error" *ngIf="!isValid">
            <mat-icon class="preview-icon">error_outline</mat-icon>
            <span>Not a valid Spring cron expression.</span>
        </div>
    </div>

    <mat-divider class="my-3"></mat-divider>

    <!-- ── Wait-for-previous toggle (lives below all modes — applies to all) ── -->
    <div class="dependency-row">
        <mat-slide-toggle [formControl]="dependency" color="primary">
            Wait for previous execution to complete?
        </mat-slide-toggle>
        <mat-icon class="help-icon" matTooltip="When ON: the next event re-anchors to the previous execution's end + interval (no overlap). When OFF: events fire on the calendar cadence regardless of whether the previous one finished.">
            help_outline
        </mat-icon>
    </div>

    <!-- ── Help accordion (kept — power-user reference) ────────────────────── -->
    <mat-accordion class="mt-3">
        <mat-expansion-panel>
            <mat-expansion-panel-header>
                <mat-panel-title>📘 Cron Expression Reference — Spring 6-field syntax</mat-panel-title>
            </mat-expansion-panel-header>

            <div class="cron-help-content">
                <div class="cron-guide">
                    <h3>Spring CronExpression — 6 fields</h3>
                    <p>This scheduler uses Spring's <code>CronExpression</code>. Six fields, separated by single spaces:</p>
<pre>┌───────────── second        (0-59)
│ ┌───────────── minute        (0-59)
│ │ ┌───────────── hour          (0-23)
│ │ │ ┌───────────── day of month (1-31)
│ │ │ │ ┌───────────── month        (1-12 or JAN-DEC)
│ │ │ │ │ ┌───────────── day of week  (0-7 or MON-SUN; both 0 and 7 are Sunday)
│ │ │ │ │ │
*  *  *  *  *  *</pre>

                    <h4>Macros (whole expression)</h4>
                    <ul>
                        <li><code>&#64;yearly</code> / <code>&#64;annually</code> — once a year (Jan 1, 00:00:00)</li>
                        <li><code>&#64;monthly</code> — first day of every month at 00:00:00</li>
                        <li><code>&#64;weekly</code> — every Sunday at 00:00:00</li>
                        <li><code>&#64;daily</code> / <code>&#64;midnight</code> — every day at 00:00:00</li>
                        <li><code>&#64;hourly</code> — top of every hour</li>
                    </ul>

                    <h4>Special characters</h4>
                    <ul>
                        <li><code>*</code> — every value in the field</li>
                        <li><code>?</code> — no specific value (interchangeable with <code>*</code> in day-of-month / day-of-week)</li>
                        <li><code>,</code> — list (e.g. <code>MON,WED,FRI</code>)</li>
                        <li><code>-</code> — range (e.g. <code>10-12</code>)</li>
                        <li><code>/</code> — increments (e.g. <code>0/15</code> = every 15 starting from 0; <code>*/5</code> = every 5)</li>
                        <li><code>L</code> — last (in day-of-month: <code>L</code> = last day of month; in day-of-week: <code>5L</code> = last Friday)</li>
                        <li><code>#</code> — nth weekday of month (e.g. <code>MON#1</code> = first Monday)</li>
                    </ul>

                    <h4>Examples</h4>
                    <ul>
                        <li><code>0 0 12 * * *</code> — every day at 12:00:00</li>
                        <li><code>0 */15 * * * *</code> — every 15 minutes (default)</li>
                        <li><code>0 0 9 * * MON-FRI</code> — 09:00 on weekdays</li>
                        <li><code>0 30 10 ? * MON#1</code> — 10:30 on the first Monday of each month</li>
                        <li><code>0 0 0 L * *</code> — midnight on the last day of every month</li>
                    </ul>

                    <h4>Not supported</h4>
                    <ul>
                        <li>5-field POSIX cron (<code>* * * * *</code>) — Spring requires 6 fields.</li>
                        <li>7-field Quartz format (with year) — not parsed.</li>
                        <li><code>W</code> (nearest weekday) — Quartz-only.</li>
                    </ul>

                    <p>📘 Reference:
                        <a href="https://docs.spring.io/spring-framework/docs/current/javadoc-api/org/springframework/scheduling/support/CronExpression.html"
                           target="_blank">Spring CronExpression Javadoc</a>
                    </p>
                </div>
            </div>
        </mat-expansion-panel>
    </mat-accordion>
</div>
